import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, ImageOff, Loader2 } from "lucide-react";
import { publicApi, fileUrl, PLACEHOLDER_IMG } from "@/lib/api";
import { CATEGORIES, CATEGORY_MAP } from "@/data/categories";
import { TECHNICAL_IMAGE_INDEXES } from "@/data/technicalSheets";

export default function Catalog() {
  const { categorySlug } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    publicApi.get("/products", { params: categorySlug ? { category: categorySlug } : {} })
      .then((res) => setProducts(Array.isArray(res.data) ? res.data : []))
      .catch(() => { setProducts([]); setLoadError(true); })
      .finally(() => setLoading(false));
  }, [categorySlug]);

  const currentCat = categorySlug ? CATEGORY_MAP[categorySlug] : null;

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Header */}
      <section className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="font-mono text-xs tracking-[0.3em] uppercase text-amber-500 mb-3">
            Catálogo de productos
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter">
            {currentCat ? currentCat.name : "Todos los equipos"}
          </h1>
          {currentCat && (
            <p className="mt-4 text-slate-400 text-lg max-w-2xl">{currentCat.blurb}</p>
          )}
        </div>
      </section>

      {/* Category filter bar */}
      <div className="border-b-2 border-slate-950 bg-white sticky top-16 md:top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3">
          <label className="md:hidden block">
            <span className="sr-only">Filtrar por categoría</span>
            <select
              value={categorySlug || ""}
              onChange={(event) => navigate(event.target.value ? `/catalogo/${event.target.value}` : "/catalogo")}
              className="w-full border-2 border-slate-950 bg-white px-4 py-3 font-display font-bold uppercase text-sm tracking-wider focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="">Todos los equipos</option>
              {CATEGORIES.map((category) => (
                <option key={category.slug} value={category.slug}>{category.name}</option>
              ))}
            </select>
          </label>
          <div className="hidden md:flex flex-wrap items-center justify-center gap-2">
            <Link
              to="/catalogo"
              data-testid="filter-all"
              className={`px-3 py-2 font-display font-bold uppercase text-[11px] tracking-wider whitespace-nowrap border-2 transition-colors ${
                !categorySlug
                  ? "bg-slate-950 text-white border-slate-950"
                  : "bg-white text-slate-600 border-transparent hover:border-slate-300"
              }`}
            >
              Todos
            </Link>
            {CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={`/catalogo/${c.slug}`}
                data-testid={`filter-${c.slug}`}
                className={`px-3 py-2 font-display font-bold uppercase text-[11px] tracking-wider whitespace-nowrap border-2 transition-colors ${
                  categorySlug === c.slug
                    ? "bg-amber-500 text-slate-950 border-slate-950"
                    : "bg-white text-slate-600 border-transparent hover:border-slate-300"
                }`}
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        {loading ? (
          <div className="flex items-center justify-center py-32 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : loadError ? (
          <div className="text-center py-24 border-2 border-dashed border-red-300 bg-white" role="alert" data-testid="catalog-error">
            <ImageOff className="w-12 h-12 mx-auto text-red-500 mb-4" />
            <p className="font-display text-xl uppercase tracking-tight text-slate-700">No se pudo cargar el catálogo</p>
            <p className="text-slate-500 mt-2 text-sm">Comprueba tu conexión e intenta nuevamente.</p>
            <button type="button" onClick={() => window.location.reload()} className="inline-block mt-6 bg-amber-500 hover:bg-amber-400 text-slate-950 font-display font-black uppercase tracking-wider px-6 py-3 text-sm btn-brutal">
              Reintentar
            </button>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-24 border-2 border-dashed border-slate-300 bg-white" data-testid="catalog-empty">
            <ImageOff className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <p className="font-display text-xl uppercase tracking-tight text-slate-700">
              No hay productos en esta categoría
            </p>
            <p className="text-slate-500 mt-2 text-sm">Pronto agregaremos nuevos equipos. Mientras tanto, puedes cotizar directamente.</p>
            <Link to="/contacto" className="inline-block mt-6 bg-amber-500 hover:bg-amber-400 text-slate-950 font-display font-black uppercase tracking-wider px-6 py-3 text-sm btn-brutal" data-testid="empty-cotizar-btn">
              Solicitar cotización
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((p) => {
              const technicalIndexes = new Set(TECHNICAL_IMAGE_INDEXES[p.name] || []);
              const cover = p.images?.find((_, index) => index > 0 && !technicalIndexes.has(index)) || p.images?.[0];
              const imgSrc = cover ? fileUrl(cover.storage_path) : PLACEHOLDER_IMG;
              const catName = CATEGORY_MAP[p.category]?.name || p.category;
              return (
                <Link
                  key={p.id}
                  to={`/producto/${p.id}`}
                  data-testid={`product-card-${p.id}`}
                  className="group bg-white border border-slate-200 hover:border-amber-500 hover:shadow-[6px_6px_0px_0px_rgba(15,23,42,0.15)] transition-all flex flex-col"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-slate-100 border-b border-slate-200">
                    <img
                      src={imgSrc}
                      alt={p.name}
                      onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMG; }}
                      className="w-full h-full object-contain p-2 group-hover:scale-[1.02] transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-amber-600 mb-2">
                      {catName}
                    </div>
                    <div className="font-display font-bold uppercase tracking-tight text-lg text-slate-950 leading-tight">
                      {p.name}
                    </div>
                    {p.short_description && (
                      <p className="text-sm text-slate-500 mt-2 line-clamp-2 leading-relaxed">{p.short_description}</p>
                    )}
                    <div className="mt-4 flex items-center justify-between pt-4 border-t border-slate-100">
                      <span className="text-xs font-display font-bold uppercase tracking-wider text-slate-950 group-hover:text-amber-600 transition-colors">
                        Ver detalle
                      </span>
                      <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-amber-600 group-hover:translate-x-1 transition-all" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
